import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  announcementOnPage,
  dismissAnnouncement,
  readMyAnnouncements,
  type ShownAnnouncement,
} from '@shared/services/adminService'
import './announcement.css'

/**
 * Announcements, over the website (Administrator skill, Huanrui, 2026-10-09)
 * -------------------------------------------------------------
 * One at a time, on the pages each was meant for. Closed once, never shown
 * to that person again (the server remembers). Asked again every ten
 * minutes and when the window comes back into focus, so one sent during a
 * visit still arrives.
 */

const ASK_EVERY = 10 * 60 * 1000

/** The announcement itself, as everybody sees it: also drawn in the Administrator page's preview. */
export function AnnouncementSheet({
  body,
  image,
  onClose,
  closeLabel = 'Got it',
  autoFocus = false,
}: {
  body: string
  image: string
  onClose?: () => void
  closeLabel?: string
  autoFocus?: boolean
}) {
  return (
    <div className="ann__card">
      {image ? <img className="ann__image" src={image} alt="" /> : null}
      {body ? <p className="ann__body">{body}</p> : null}
      <div className="ann__foot">
        <button type="button" className="ann__close" onClick={onClose} autoFocus={autoFocus} tabIndex={onClose ? 0 : -1}>
          {closeLabel}
        </button>
      </div>
    </div>
  )
}

export function AnnouncementCard({
  body,
  image,
  onClose,
  closeLabel = 'Got it',
}: {
  body: string
  image: string
  onClose: () => void
  closeLabel?: string
}) {
  return (
    <div className="ann" role="dialog" aria-modal="true" aria-label="Announcement">
      <AnnouncementSheet body={body} image={image} onClose={onClose} closeLabel={closeLabel} autoFocus />
    </div>
  )
}

export function AnnouncementPopup() {
  const { pathname } = useLocation()
  const [list, setList] = useState<ShownAnnouncement[]>([])
  const [closed, setClosed] = useState<Set<string>>(new Set())

  const ask = useCallback(() => {
    readMyAnnouncements()
      .then(setList)
      .catch(() => {
        // Nothing to show is the safe answer.
      })
  }, [])

  useEffect(() => {
    ask()
    const timer = window.setInterval(ask, ASK_EVERY)
    window.addEventListener('focus', ask)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', ask)
    }
  }, [ask])

  const shown = list.find((one) => !closed.has(one.id) && announcementOnPage(one.pages, pathname))
  if (!shown) return null

  return (
    <AnnouncementCard
      body={shown.body}
      image={shown.image}
      onClose={() => {
        setClosed((was) => new Set(was).add(shown.id))
        void dismissAnnouncement(shown.id).catch((error: unknown) => console.warn('could not close the announcement', error))
      }}
    />
  )
}
